import { useEffect, useState } from "react";

/** COMPONENT */
import { Box, CircularProgress, Typography } from "@mui/material";
import ModalCustom from "../../../component/modalCustom/modalCustom";
import { notiError } from "../../../component/notification/notifications";
import DeductTable from "../components/DeductTable";

/** API */
import DepositRefundApi, {
  DepositRefundDetail,
  DepositRefundSide,
} from "../../../api/deposit-refund";

/** UTILS */
import { colors } from "../../../constants/colors";
import {
  depositRefundStatusBg,
  depositRefundStatusColor,
  depositRefundStatusLabel,
} from "../../../constants/depositRefund";
import { dateTimeToView } from "../../../utils/date.utils";
import {
  DASH,
  bahtToView,
  dateOnlyToView,
  getDepositRefundError,
  toAmount,
} from "../depositRefund.utils";

/** STYLE */
import * as Style from "../depositRefund.style";

type Props = {
  id: number;
  side: DepositRefundSide;
  onClose: () => void;
};

type RowKey =
  | "deposit_refund_no"
  | "lease_agreement_no"
  | "tenant_name"
  | "zone_name"
  | "stall_name"
  | "stall_number"
  | "stall_type_name"
  | "lease_status_name"
  | "lease_date_end"
  | "request_amount"
  | "approved_amount"
  | "refund_method_name"
  | "request_date"
  | "request_by_name"
  | "request_note"
  | "status"
  | "reviewed_date"
  | "reviewed_name"
  | "refund_date"
  | "doc_ref"
  | "review_note";

const textOrDash = (value?: string | null) => value || DASH;

type RowRenderer = {
  label: string;
  value: (detail: DepositRefundDetail, side: DepositRefundSide) => React.ReactNode;
  /** true = ไม่แสดงแถวเลยถ้าไม่มีค่า (แถวว่างเปล่าไม่ได้ให้ข้อมูลอะไร) */
  hideWhenEmpty?: boolean;
  isEmpty?: (detail: DepositRefundDetail) => boolean;
};

const RENDERERS: Record<RowKey, RowRenderer> = {
  deposit_refund_no: {
    label: "เลขที่คำร้อง",
    value: (d) => textOrDash(d.deposit_refund_no),
  },
  lease_agreement_no: {
    label: "เลขที่สัญญา",
    value: (d) => textOrDash(d.lease_agreement_no),
  },
  tenant_name: { label: "ชื่อผู้เช่าแผงค้า", value: (d) => textOrDash(d.tenant_name) },
  zone_name: { label: "โซน", value: (d) => textOrDash(d.zone_name) },
  stall_name: { label: "แผงค้า", value: (d) => textOrDash(d.stall_name) },
  stall_number: { label: "เลขแผงค้า", value: (d) => textOrDash(d.stall_number) },
  stall_type_name: { label: "ประเภทแผงค้า", value: (d) => textOrDash(d.stall_type_name) },
  lease_status_name: {
    label: "สถานะสัญญา",
    // backend คืนคำไทยมาให้แล้ว ไม่ต้อง map เอง
    value: (d) => textOrDash(d.lease_status_name || d.lease_status),
  },
  lease_date_end: {
    label: "วันที่หมดสัญญา",
    // DATE ที่ serialize เป็น UTC ISO ต้อง format ด้วย timezone Asia/Bangkok
    value: (d) => dateOnlyToView(d.lease_date_end),
  },
  request_amount: { label: "ยอดเงินที่ขอคืน", value: (d) => bahtToView(d.request_amount) },
  approved_amount: {
    label: "ยอดเงินที่ได้รับการพิจารณา",
    value: (d) => bahtToView(d.approved_amount),
    // ยังไม่พิจารณา หรือไม่อนุมัติ = ไม่มียอด ไม่ต้องโชว์แถว
    hideWhenEmpty: true,
    isEmpty: (d) => toAmount(d.approved_amount) === null,
  },
  refund_method_name: {
    label: "ช่องทางการคืนเงิน",
    value: (d) => textOrDash(d.refund_method_name),
    hideWhenEmpty: true,
    isEmpty: (d) => !d.refund_method_name,
  },
  request_date: {
    label: "วันเวลาที่ส่งคำร้อง",
    // dateTimeToView(undefined) format วันนี้เงียบๆ ต้อง guard ก่อนเสมอ
    value: (d) => (d.request_date ? dateTimeToView(d.request_date) : DASH),
  },
  request_by_name: {
    label: "ผู้ยื่นคำร้อง",
    value: (d) => textOrDash(d.request_by_name),
    hideWhenEmpty: true,
    isEmpty: (d) => !d.request_by_name,
  },
  request_note: {
    label: "หมายเหตุการยื่นคำร้อง",
    value: (d) => textOrDash(d.request_note),
    hideWhenEmpty: true,
    isEmpty: (d) => !d.request_note,
  },
  status: {
    label: "สถานะ",
    value: (d, side) => (
      <Style.StatusChip
        chipColor={depositRefundStatusColor(d.status)}
        chipBg={depositRefundStatusBg(d.status)}
      >
        {depositRefundStatusLabel(d.status, side === "CUSTOMER")}
      </Style.StatusChip>
    ),
  },
  reviewed_date: {
    label: "วันเวลาที่พิจารณา",
    value: (d, side) => {
      if (d.reviewed_date) return dateTimeToView(d.reviewed_date);
      // ฝั่งลูกค้าอยากรู้ว่ายังไม่พิจารณา ไม่ใช่แค่ขีดที่ตีความไม่ได้
      return side === "CUSTOMER" ? "ยังไม่พิจารณา" : DASH;
    },
  },
  reviewed_name: { label: "ผู้พิจารณา", value: (d) => textOrDash(d.reviewed_name) },
  refund_date: {
    label: "วันที่คืนเงิน",
    value: (d) => dateOnlyToView(d.refund_date),
    hideWhenEmpty: true,
    isEmpty: (d) => !d.refund_date,
  },
  doc_ref: {
    label: "เลขที่เอกสารอ้างอิง",
    value: (d) => textOrDash(d.doc_ref),
    hideWhenEmpty: true,
    isEmpty: (d) => !d.doc_ref,
  },
  review_note: {
    label: "รายละเอียดการพิจารณา",
    value: (d) => textOrDash(d.review_note),
    hideWhenEmpty: true,
    isEmpty: (d) => !d.review_note,
  },
};

/**
 * แถวที่แต่ละฝั่งเห็น — ประกาศเป็นข้อมูล ไม่ใช่เงื่อนไขกระจายใน JSX
 * ฝั่งลูกค้าไม่มี tenant_name (เป็นคำร้องของตัวเองอยู่แล้ว)
 * และไม่มี reviewed_name / request_by_name (PDPA — ไม่ควรเห็นชื่อเจ้าหน้าที่)
 */
const ROWS_BY_SIDE: Record<DepositRefundSide, RowKey[]> = {
  ADMIN: [
    "deposit_refund_no",
    "lease_agreement_no",
    "tenant_name",
    "zone_name",
    "stall_name",
    "stall_number",
    "stall_type_name",
    "lease_status_name",
    "lease_date_end",
    "request_amount",
    "refund_method_name",
    "request_date",
    "request_by_name",
    "request_note",
    "status",
    "reviewed_date",
    "reviewed_name",
    "approved_amount",
    "refund_date",
    "doc_ref",
    "review_note",
  ],
  CUSTOMER: [
    "deposit_refund_no",
    "lease_agreement_no",
    "zone_name",
    "stall_name",
    "stall_number",
    "lease_date_end",
    "request_amount",
    "refund_method_name",
    "request_date",
    "status",
    "reviewed_date",
    "approved_amount",
    "refund_date",
    "review_note",
  ],
};

const TITLE_BY_SIDE: Record<DepositRefundSide, string> = {
  ADMIN: "รายละเอียดคำร้องคืนค่ามัดจำ",
  CUSTOMER: "รายละเอียดคำร้องขอคืนค่ามัดจำ",
};

export default function ModalDepositRefundDetail({ id, side, onClose }: Props) {
  const [detail, setDetail] = useState<DepositRefundDetail | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    /** กัน setState หลัง unmount ตอนผู้ใช้ปิด modal ก่อน response กลับมา */
    let isMounted = true;

    const loadDetail = async () => {
      setIsLoading(true);
      try {
        const res = await DepositRefundApi.findById(side, id);
        if (!isMounted) return;
        setDetail(res.data?.data || null);
      } catch (error) {
        if (!isMounted) return;
        setDetail(null);
        notiError(getDepositRefundError(error, "ดึงรายละเอียดคำร้องไม่สำเร็จ"));
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadDetail();
    return () => {
      isMounted = false;
    };
  }, [id, side]);

  const renderRow = (key: RowKey, label: string, value: React.ReactNode) => (
    <Style.DetailRow key={key}>
      <Typography sx={{ fontSize: 16, color: colors.textLightGray }}>{label}</Typography>
      <Box sx={{ fontSize: 16, wordBreak: "break-word", whiteSpace: "pre-line" }}>{value}</Box>
    </Style.DetailRow>
  );

  const renderBody = () => {
    if (isLoading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 6 }}>
          <CircularProgress size={32} sx={{ color: colors.themeMainColor }} />
        </Box>
      );
    }

    if (!detail) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 6 }}>
          <Typography sx={{ color: colors.textLightGray }}>
            ไม่พบคำร้องขอคืนค่ามัดจำที่ต้องการ
          </Typography>
        </Box>
      );
    }

    const rows = ROWS_BY_SIDE[side].filter((key) => {
      const renderer = RENDERERS[key];
      if (!renderer.hideWhenEmpty) return true;
      return !renderer.isEmpty?.(detail);
    });

    return (
      <Box
        className="custom-scroll"
        sx={{ px: { xs: 2, md: 3 }, pb: 1, maxHeight: "calc(100vh - 220px)", overflowY: "auto" }}
      >
        <Box
          sx={{
            border: `1px solid ${colors.lightGray}`,
            borderRadius: "8px",
            backgroundColor: colors.white,
          }}
        >
          {rows.map((key) =>
            renderRow(key, RENDERERS[key].label, RENDERERS[key].value(detail, side))
          )}
        </Box>

        {/* deducts เป็น [] เมื่อยังไม่พิจารณา หรือไม่อนุมัติ — DeductTable คืน null ให้เอง */}
        <Box sx={{ mt: 3 }}>
          <DeductTable deducts={detail.deducts} approvedAmount={detail.approved_amount} />
        </Box>
      </Box>
    );
  };

  return (
    <ModalCustom
      // ต้องเป็น true เสมอ + ให้ parent conditional render (ค่า false จะ fall back ไปอ่าน redux)
      isShowProps
      // ปิด modal เองผ่าน onClose ไม่ให้ไป reset redux modal.isShow ของ modal อื่นในหน้าเดียวกัน
      autoResetModal={false}
      size="lg"
      closeButton
      title={TITLE_BY_SIDE[side]}
      component={renderBody()}
      // ไม่ส่ง onSubmit/textBtnConfirm footer จึงไม่มีปุ่ม เหลือแค่ X มุมขวาบนตามดีไซน์
      onClose={onClose}
    />
  );
}
